// Cabecera de una jornada del multievento en la pestaña Líneas y menús del
// formato nuevo: número, día y fecha, horas (insignia "+1" si acaba al día
// siguiente), personas (PersonasJornada), lugar y el menú de acciones (Editar
// jornada, Editar menús, Duplicar, Eliminar y las dos órdenes de cocina). En
// solo lectura quedan solo las descargas de las órdenes.
// ObservacionesJornada: las observaciones de la jornada editables en línea
// (se guardan al salir del campo si cambiaron).
import React, { useEffect, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import PersonasJornada from './PersonasJornada';
import { TEXTO_ORDEN_NUEVA_PRUEBAS } from '../../utils/tareasPresupuesto';
import { horasJornada } from '../../utils/multieventoPantallas';

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const fechaCorta = (iso) => {
  const t = String(iso || '').slice(0, 10);
  if (t.length !== 10) return 'Sin fecha';
  const d = new Date(`${t}T12:00:00`);
  return `${DIAS[d.getDay()]} ${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(0, 4)}`;
};

const insignia = 'inline-flex items-center rounded-full border px-2 py-0.5 text-xs md:text-[11px] font-semibold';

export const textoLugarJornada = (j) => {
  const propio = String(j?.lugar_evento || j?.direccion_exacta || '').trim();
  return propio || 'Misma dirección principal';
};

const JornadaCabecera = ({
  presupuesto, jornada, numero, vacia = false, facturada = false, soloLectura = false, ocupado = false,
  onActualizado, onEditar, onEditarMenus, onDuplicar, onEliminar, onOrdenCocina, onOrdenCocinaNueva,
}) => {
  const j = jornada || {};
  const horas = horasJornada(j);
  const editable = !soloLectura && !facturada;
  const acciones = [
    ...(editable ? [
      { clave: 'editar', etiqueta: 'Editar jornada', onSelect: onEditar },
      { clave: 'menus', etiqueta: 'Editar menús', onSelect: onEditarMenus },
      { clave: 'duplicar', etiqueta: 'Duplicar', onSelect: onDuplicar },
    ] : []),
    { clave: 'orden-nueva', etiqueta: 'Orden de cocina (nueva)', detalle: TEXTO_ORDEN_NUEVA_PRUEBAS, onSelect: onOrdenCocinaNueva, separar: editable },
    { clave: 'orden-antigua', etiqueta: 'Orden de cocina (antigua)', onSelect: onOrdenCocina },
    ...(editable ? [{ clave: 'eliminar', etiqueta: 'Eliminar jornada', onSelect: onEliminar, peligro: true, separar: true }] : []),
  ].filter(a => typeof a.onSelect === 'function');

  return (
    <div className="flex flex-wrap items-start justify-between gap-3" data-testid={`jornada-cabecera-${j.id}`}>
      <div className="min-w-0 flex flex-col gap-1">
        <p className="font-bold text-[#16221D] flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>Jornada {numero} · {fechaCorta(j.fecha)}</span>
          {horas.texto && (
            <span className="font-semibold text-[#33443C] tabular-nums">· {horas.texto}</span>
          )}
          {horas.masUno && (
            <span className={`${insignia} bg-orange-50 border-orange-200 text-orange-800`} title="Termina al día siguiente">+1</span>
          )}
          {vacia && (
            <span className={`${insignia} bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]`} data-testid={`jornada-vacia-${j.id}`}>
              Sin líneas
            </span>
          )}
          {facturada && (
            <span className={`${insignia} bg-[#EEF1EF] border-[#CBD3CF] text-[#4A5A53]`}>Facturada</span>
          )}
        </p>
        <p className="text-xs text-[#5B6B64]" data-testid={`jornada-lugar-${j.id}`}>
          Lugar: {textoLugarJornada(j)}
          {j.hora_llegada_estimada ? ` · llegada ${j.hora_llegada_estimada}` : ''}
        </p>
      </div>
      <div className="flex items-end gap-2">
        {editable ? (
          <PersonasJornada presupuestoId={presupuesto?.id} jornada={j} compacto onActualizado={onActualizado} />
        ) : (
          <p className="text-sm text-[#33443C]">{Number(j.num_personas) || 0} personas</p>
        )}
        {acciones.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={ocupado}
                className="h-11 md:h-9 px-3 md:px-2.5 rounded-lg border border-[#CBD3CF] bg-white text-[#16221D] flex items-center gap-1 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                data-testid={`jornada-acciones-${j.id}`}
              >
                <MoreHorizontal className="w-4 h-4" />
                <span>Acciones</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white w-[calc(100vw-2rem)] max-w-sm md:w-64 md:max-w-none">
              {acciones.map(a => (
                <React.Fragment key={a.clave}>
                  {a.separar && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onSelect={() => a.onSelect()}
                    className={`cursor-pointer flex flex-col items-start py-3 md:py-1.5 text-[15px] md:text-sm ${a.peligro ? 'text-red-700 focus:text-red-800' : ''}`}
                    data-testid={`jornada-accion-${a.clave}-${j.id}`}
                  >
                    <span>{a.etiqueta}</span>
                    {a.detalle && <span className="text-[13px] md:text-[11px] text-[#5B6B64] leading-tight">{a.detalle}</span>}
                  </DropdownMenuItem>
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

export const ObservacionesJornada = ({ jornada, soloLectura = false, onGuardar }) => {
  const guardadas = jornada?.observaciones || '';
  const [valor, setValor] = useState(guardadas);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setValor(guardadas); }, [guardadas, jornada?.id]);

  if (soloLectura) {
    return guardadas ? (
      <p className="text-xs text-[#33443C] whitespace-pre-line"><span className="font-semibold">Observaciones:</span> {guardadas}</p>
    ) : null;
  }
  const alSalir = async () => {
    if (valor === guardadas || guardando) return;
    setGuardando(true);
    const ok = await onGuardar({ observaciones: valor });
    setGuardando(false);
    if (!ok) setValor(guardadas);
  };
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold text-[#5B6B64]">
      Observaciones de la jornada {guardando ? '(guardando...)' : ''}
      <textarea
        rows={2}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onBlur={alSalir}
        disabled={guardando}
        placeholder="Instrucciones especiales, alergias, requisitos... (salen en la orden de cocina de esta jornada)"
        className="w-full px-2.5 py-1.5 text-base md:text-sm font-normal text-[#16221D] border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary resize-y"
        data-testid={`jornada-observaciones-${jornada?.id}`}
      />
    </label>
  );
};

export default JornadaCabecera;
